<template>
  <div class="interactives-push-alert-animation-timeline">
  </div>
</template>

<script>
import * as d3 from 'd3'
import D3Svg from '../../../interactives-d3-svg/src/App.vue'

export default {
  name: 'timeline',
  extends: D3Svg,
  props: ['firstSimSec', 'moments', 'eras', 'lastSimSec', 'currentSimSec', 'lag', 'animReady'],
  data () {
    return {
      mouseX: 0, // mouse x-coord
      brushX: -4,
      brushWidth: 8,
      momentRadius: 3
    }
  },
  computed: {
    // timelineStart () { return d3.timeMonth.floor(this.firstSimSec) }
    timelineStart () { return d3.timeDay.offset(this.firstSimSec, -1) }
  },
  watch: {
    animReady: function (newVal) { if (newVal) this.$emit('ready') },
    currentSimSec: function (newVal) { if (this.brush) this.updateBrush() }
  },
  mounted () {
    this.$parent.$on('resize', this.reRenderViz)
  },
  methods: {
    renderViz () {
      this.xScale = d3.scaleTime()
        .domain([this.timelineStart, this.lastSimSec])
        .range([0, this.vizWidth])

      const ticks = d3.merge([[this.timelineStart], d3.timeMonth.every(1).range(this.firstSimSec, this.lastSimSec)])

      const tickFormatter = d3.timeFormat('%B')
      const xAxis = d3.axisBottom(this.xScale)
        .tickValues(ticks)
        .tickSize(0)
        .tickPadding(11)
        .tickFormat((d, i) => {
          if (i === 0) return 'Nov. 8'
          else return tickFormatter(d)
        })

      this.viz.append('g')
        .attr('class', 'timeline-axis')
        .attr('transform', `translate(0, ${this.vizHeight})`)
        .call(xAxis)

      // timeline
      this.viz.append('rect')
        .attr('class', 'timeline')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.vizWidth)
        .attr('height', this.vizHeight)
        .attr('fill', 'white')
        .attr('fill-opacity', 1)

      // eras
      this.viz.selectAll('rect.era-rect')
        .data(this.eras).enter()
        .append('rect')
        .attr('class', 'era-rect')
        .attr('x', d => this.xScale(d.startParsed))
        .attr('y', 0)
        .attr('width', d => this.xScale(d.endParsed) - this.xScale(d.startParsed))
        .attr('height', this.vizHeight)

      this.viz.selectAll('text.era-label')
        .data(this.eras).enter()
        .append('text')
        .attr('class', 'era-label')
        .attr('x', d => (this.xScale(d.startParsed) + this.xScale(d.endParsed)) / 2)
        .attr('y', this.vizHeight / 2)
        .attr('dy', '0.35em')
        .text(d => d.eventname)

      // moments
      const lines = this.viz.append('g')
      const labels = this.viz.append('g')
      const circles = this.viz.append('g')

      var prev = 1
      var multiplier = 0
      var label = labels.selectAll('text.moment-label')

      label = label.data(this.moments).enter()
        .append('text')
        .attr('class', 'moment-label')
        .attr('x', d => this.xScale(d.startParsed))
        .attr('y', (d, i) => {
          while (multiplier === prev) {
            multiplier = (Math.floor(3 * Math.random()))
          }
          prev = multiplier
          return (multiplier + 1.25) * -this.margins.top / 4
        })
        .text(d => d.eventname)

      const nodes = label.nodes()
      const cross = d3.cross(nodes, nodes)
      cross.forEach(pair => {
        const a = pair[0]
        const b = pair[1]
        if (a !== b && this.checkOverlap(a.getBBox(), b.getBBox())) {
          if (d3.select(a).datum().rank > d3.select(b).datum().rank) {
            d3.select(a).style('display', 'none')
          } else d3.select(b).style('display', 'none')
        }
      })

      const boxes = nodes.map(node => node.getBBox())
      lines.selectAll('line.moment-line')
        .data(boxes).enter()
        .append('line')
        .attr('class', 'moment-line')
        .attr('x1', d => d.x + d.width / 2)
        .attr('x2', d => d.x + d.width / 2)
        .attr('y1', d => d.y + d.height + 3)
        .attr('y2', -this.momentRadius)

      circles.selectAll('circle.moment')
        .data(boxes).enter()
        .append('circle')
        .attr('class', 'moment')
        .attr('cx', d => d.x + d.width / 2)
        .attr('cy', 0)
        .attr('r', this.momentRadius)

      // timeline ghost
      const ghost = this.viz.append('rect')
        .attr('class', 'ghost')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.vizWidth)
        .attr('height', this.vizHeight)
        .attr('fill', 'white')
        .attr('fill-opacity', 0)

      this.brush = this.viz.append('line')
        .attr('class', 'timeline-brush')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', 0)
        .attr('y2', this.vizHeight)
        .attr('stroke-width', this.brushWidth)
        .attr('stroke', 'url(#radial)')

      const drag = d3.drag()
        .on('start', () => {
          this.brush.classed('dragging', true)
          this.$emit('dragStarted')
        })
        .on('drag', () => {
          this.setNewBrushX(d3.mouse(this.viz.node())[0])
        })
        .on('end', () => {
          this.brush.classed('dragging', false)
          this.updateSelectedSimSec()
          this.$emit('dragEnded')
        })

      this.$parent.$on('enableDrag', () => {
        this.brush.call(drag).classed('draggable', true)

        ghost.on('mousedown', () => {
          this.$emit('dragStarted')
        }).on('mouseup', () => {
          this.setNewBrushX(d3.mouse(this.viz.node())[0])
          this.updateSelectedSimSec()
          this.$emit('dragEnded')
        }).classed('interactive', true)

        label.on('mouseover', function () {
          d3.select(this).interrupt().transition().style('fill', '#F8E71C')
        }).on('mouseout', function () {
          d3.select(this).interrupt().transition().style('fill', 'white')
        }).on('mousedown', () => {
          this.$emit('dragStarted')
        }).on('mouseup', (d) => {
          this.setNewBrushX(this.xScale(d.startParsed))
          this.updateSelectedSimSec()
          this.$emit('dragEnded')
        }).classed('interactive', true)
      })
      this.showViz()
    },
    setNewBrushX (input) {
      var newX = d3.min( // right edge can't transgress end of timeline
        [
          d3.max( // left edge can't transgress beginning of timeline
            [
              input,
              this.xScale(this.firstSimSec)
            ]
          ), this.vizWidth
        ]
      )
      this.brush
        .attr('x1', newX)
        .attr('x2', newX)
    },
    updateSelectedSimSec () {
      const newTime = this.xScale.invert(parseFloat(this.brush.attr('x1')))
      this.$emit('update:selectedSimSec', newTime)
    },
    updateBrush () {
      const newX = this.xScale(this.currentSimSec)

      this.brush
        .transition()
        .duration(this.lag)
        .ease(d3.easeLinear)
        .attr('x1', newX)
        .attr('x2', newX)
    },
    reRenderViz () {
      this.hideViz()
      this.svg.remove()
      this.setUp()
      this.renderViz()
    },
    showViz () { this.viz.transition().style('opacity', 1) },
    hideViz () { this.viz.transition().style('opacity', 0) },
    checkOverlap (rect1, rect2) {
      const horizontalOverlap = !(rect1.x + rect1.width < rect2.x ||
        rect1.x > rect2.x + rect2.width)

      const verticalOverlap = !(rect1.y + rect1.height < rect2.y ||
        rect1.y > rect2.y + rect2.height)

      return horizontalOverlap & verticalOverlap
    }
  }
}
</script>

<style lang="scss">
.interactives-push-alert-animation-timeline {
  width: 100%;
  svg text {
    font-family: 'Helvetica Neue';
  }
  .interactives-push-alert-animation-timeline-viz {
    opacity: 0;
  }
  .alert-circle {
    fill: black;
  }
  .timeline-brush {
    stroke: #F8E71C;
    stroke-opacity: 1;
    stroke-linecap: round;
  }
  rect.heat {
    fill-opacity: 0.5;
  }
  .timeline-axis .domain {
    stroke-opacity: 0;
  }
  .timeline {
    fill: #4C7A92;
  }
  .timeline-axis .tick text {
    fill: #CCCCCC;
    text-transform: uppercase;
  }
  .ghost {
    &.interactive {
      cursor: crosshair;
    }
  }
  .era-rect {
    fill: #6A9AB2;
  }
  .era-label, .moment-label {
    text-anchor: middle;
    font-size: 0.8em;
    fill: white;
    font-weight: 200;
  }
  .moment-label.interactive {
    cursor: pointer;
  }
  .moment {
    fill: white;
    fill-opacity: 0.5;
  }
  .moment-line {
    stroke: white;
    stroke-width: 1;
    stroke-opacity: 0.5;
  }

  svg text {
    font-family: 'sl-Apres';
  }
}
</style>



// WEBPACK FOOTER //
// Timeline.vue?72c42c27